import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";
import "animate.css";

import logo1 from "../assets/logo1.png";
import mission from "../assets/mission.webp";
import vision from "../assets/vision.webp";
import FreeTrialModal from "./FreeTrialModal";

const AboutUs = () => {
  const [showTrial, setShowTrial] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div>
      {/* SEO */}
      <Helmet>
        <title>About Screenify | AI Hiring & Recruitment Platform</title>
        <meta
          name="description"
          content="Learn about Screenify’s mission to build AI-powered hiring, resume screening and recruitment automation."
        />
        <link rel="canonical" href="https://theairecruiter.ai/about" />
      </Helmet>

      {/* Navbar */}
      <nav className="navbar navbar-expand-lg bg-white shadow-sm sticky-top">
        <div className="container">
          <Link className="navbar-brand mx-auto" to="/home">
            <img src={logo1} alt="logo" style={{ height: "50px" }} />
            <span className="web-name ms-2">Screenify</span>
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div
            className="collapse navbar-collapse justify-content-between"
            id="navbarNav"
          >
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/">
                  Home
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to="/ai-recruiter">
                  AI Recruiter
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link active fw-semibold" to="/about">
                  About us
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to="/contact">
                  Contact
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to="/blog">
                  Blog
                </Link>
              </li>

              {/* <li className="nav-item">
                <button
                  className="btn btn-warning"
                  onClick={() => setShowTrial(true)}
                >
                  Free Trial
                </button>
                <FreeTrialModal
                  show={showTrial}
                  onClose={() => setShowTrial(false)}
                />
              </li> */}
            </ul>

            <ul className="navbar-nav ms-lg-auto mt-3 mt-lg-0 text-center">
              <li className="nav-item mb-2 mb-lg-0 me-lg-2">
                <button
                  className="btn btn-warning w-100 w-lg-auto"
                  onClick={() => setShowTrial(true)}
                >
                  Free Trial
                </button>
                <FreeTrialModal
                  show={showTrial}
                  onClose={() => setShowTrial(false)}
                />
              </li>

              <li className="nav-item mb-2 mb-lg-0 me-lg-2">
                <Link
                  className="btn btn-outline-primary w-100 w-lg-auto"
                  to="/login"
                >
                  Login
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className="btn btn-outline-primary w-100 w-lg-auto"
                  to="/signup"
                >
                  Sign Up
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="text-center text-white"
        style={{
          background: "linear-gradient(135deg, #007bff, #00c6ff)",
          padding: "100px 0",
        }}
      >
        <div className="container" data-aos="fade-up">
          <h1 className="display-4 fw-bold animate__animated animate__fadeInDown">
            About Screenify
          </h1>
          <p className="lead mt-3 animate__animated animate__fadeInUp">
            Learn more about our mission, vision, and values.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6" data-aos="fade-right">
              <h2 className="fw-bold">Our Mission</h2>
              <p className="fs-5">
                At Screenify, our mission is to transform the hiring process
                through cutting-edge AI technology. We empower businesses to
                make smart, data-driven decisions and create an equitable,
                scalable recruitment process.
              </p>
            </div>
            <div className="col-md-6" data-aos="fade-left">
              <img
                src={mission}
                alt="Mission"
                className="img-fluid rounded shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 order-md-2" data-aos="fade-left">
              <h2 className="fw-bold">Our Vision</h2>
              <p className="fs-5">
                We envision a future where hiring is seamless, inclusive,
                automated, and highly efficient. AI-driven recruitment will
                eliminate bias, reduce human effort, and deliver better matches.
              </p>
            </div>
            <div className="col-md-6 order-md-1" data-aos="fade-right">
              <img
                src={vision}
                alt="Vision"
                className="img-fluid rounded shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h2 className="fw-bold mb-4" data-aos="fade-up">
            Our Core Values
          </h2>

          <div className="row g-4">
            {[
              {
                icon: "fas fa-bolt",
                title: "Innovation",
                desc: "We constantly push boundaries using modern AI.",
              },
              {
                icon: "fas fa-users",
                title: "Transparency",
                desc: "Clear communication and unbiased hiring.",
              },
              {
                icon: "fas fa-shield-alt",
                title: "Trust & Security",
                desc: "Your data stays secure and protected.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="col-md-4"
                data-aos="fade-up"
                data-aos-delay={i * 150}
              >
                <div
                  className="p-4 rounded-4 shadow bg-white hover-scale"
                  style={{ transition: "0.3s" }}
                >
                  <i className={`${item.icon} fa-3x text-primary mb-3`}></i>
                  <h5 className="fw-bold">{item.title}</h5>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-5 bg-dark">
        <div className="container text-center text-white">
          <h2
            className="fw-bold display-5"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            Get in Touch
          </h2>
          <p class="mb-4" data-aos="fade-up" data-aos-delay="300">
            We’d love to hear from you! Reach out with any questions or
            feedback.
          </p>

          <div
            className="row g-4 justify-content-center"
            style={{ borderBottom: "2px solid rgba(255, 255, 255, 0.3)" }}
          >
            {[
              {
                title: "Call Us",
                icon: "fas fa-phone-alt",
                lines: ["+91 96115 12281", "+91 90083 09255"],
                color: "text-primary",
              },
              {
                title: "Email",
                icon: "fas fa-envelope",
                lines: ["contactus@archipeltechnologies.com"],
                color: "text-warning",
              },
              {
                title: "Follow Us",
                icon: "fas fa-share-alt",
                social: true,
              },
            ].map((item, i) => (
              <div
                className="col-md-4 mb-3"
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 200}
              >
                <div
                  className="p-4 rounded-4 shadow text-center hover-scale"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <i className={`${item.icon} fa-3x mb-3 ${item.color}`}></i>
                  <h5 className="fw-bold mb-3">{item.title}</h5>

                  {item.social ? (
                    <div>
                      <a href="#" className="me-3">
                        <i className="fab fa-linkedin fa-2x text-primary"></i>
                      </a>
                      <a href="#" className="me-3">
                        <i
                          className="fab fa-instagram fa-2x"
                          style={{ color: "#E4405F" }}
                        ></i>
                      </a>
                      <a href="#">
                        <i className="fab fa-youtube fa-2x text-danger"></i>
                      </a>
                    </div>
                  ) : (
                    item.lines.map((line, idx) => (
                      <p key={idx} className="mb-1">
                        {item.icon === "fas fa-envelope" ? (
                          <a
                            href={`mailto:${line}`}
                            className="text-decoration-none text-white"
                          >
                            <i className={`${item.icon} me-2 text-warning`}></i>
                            {line}
                          </a>
                        ) : (
                          <a
                            href={`tel:${line}`}
                            className="text-decoration-none text-white"
                          >
                            <i className={`${item.icon} me-2 text-primary`}></i>
                            {line}
                          </a>
                        )}
                      </p>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="bg-white text-dark py-3"
        // style={{
        //   background: "linear-gradient(90deg, #007bff, #00c6ff)",
        // }}
      >
        <div className="container d-flex justify-content-between">
          <p className="m-0">
            © Copyright {new Date().getFullYear()} Archipel Technlogies Pvt Ltd.
            . All Rights Reserved.
          </p>

          <div>
            <Link className="text-dark me-3" to="/privacy-policy">
              Privacy & Cookies
            </Link>
            <Link className="text-dark" to="/terms-and-conditions">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>

      <style>{`
        .hover-scale:hover {
          transform: translateY(-6px);
          transition: 0.3s;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
